/**
 * InputAdapter.js - Adapter to connect mobile controls to the existing input system
 * 
 * This module coordinates between different input sources (keyboard/mouse, touch)
 * and translates them into a consistent format for the game to use.
 */

// Import mobile controls
import { createMobileControls } from '../player/MobileControls.js';

/**
 * Creates an input adapter that handles both desktop and mobile inputs
 * @param {Object} options - Configuration options
 * @returns {Object} Input adapter interface
 */
export function createInputAdapter(options = {}) {
    // Default options
    const config = {
        playerController: null,
        camera: null,
        ...options
    };
    
    // Create mobile controls instance
    const mobileControls = createMobileControls({
        playerController: config.playerController,
        camera: config.camera
    });
    
    // State tracking
    let initialized = false;
    
    /**
     * Initialize the input adapter
     */
    function initialize() {
        if (initialized) return;
        
        console.log('[InputAdapter] Initializing input adapter');
        
        // Initialize mobile controls if on mobile device
        if (mobileControls.isMobileDevice()) {
            console.log('[InputAdapter] Mobile device detected, initializing mobile controls');
            mobileControls.initialize();
        } else {
            // Add URL parameter check for forcing mobile controls on desktop
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.has('forceMobile') && urlParams.get('forceMobile') === 'true') {
                console.log('[InputAdapter] Force mobile controls enabled via URL parameter');
                mobileControls.setEnabled(true);
                mobileControls.initialize();
            }
        }
        
        initialized = true;
    }
    
    /**
     * Connect the adapter to a player controller
     * @param {Object} playerController - The player controller
     */
    function connectToPlayerController(playerController) {
        if (!playerController) return;
        
        // Store reference to player controller
        config.playerController = playerController;
        
        // Update mobile controls with player controller reference
        mobileControls.setPlayerControllerReference(playerController);
        
        console.log('[InputAdapter] Connected to player controller');
    }
    
    /**
     * Connect the adapter to a camera controller
     * @param {Object} camera - The camera controller
     */
    function connectToCamera(camera) {
        if (!camera) return;
        
        // Store reference to camera
        config.camera = camera;
        
        // Update mobile controls with camera reference
        mobileControls.setCameraReference(camera);
        
        console.log('[InputAdapter] Connected to camera');
    }
    
    /**
     * Update the input adapter (called every frame)
     */
    function update() {
        // Update mobile controls
        if (mobileControls.isEnabled()) {
            mobileControls.update();
        }
    }
    
    /**
     * Toggle mobile controls
     * @param {boolean} state - Whether to enable mobile controls
     */
    function toggleMobileControls(state) {
        if (typeof state !== 'boolean') {
            state = !mobileControls.isEnabled();
        }
        
        mobileControls.setEnabled(state);
        
        if (state && !initialized) {
            initialize();
        }
    }
    
    /**
     * Clean up the input adapter
     */
    function cleanup() {
        if (mobileControls) {
            mobileControls.cleanup();
        }
    }
    
    // Return public interface
    return {
        initialize,
        connectToPlayerController,
        connectToCamera,
        update,
        toggleMobileControls,
        cleanup,
        isMobileDevice: mobileControls.isMobileDevice
    };
} 